import React from 'react'
import TextareaAutosize from 'react-textarea-autosize';
import { useTranslation } from 'react-i18next';

import Buttons from './Buttons'
import RecordingMic from '../animation/RecordingMic';
import { typeProcess } from '../type';


type TextAreaStyle = React.ComponentProps<typeof TextareaAutosize>['style'];

interface textAreaProps {
    label: string
    rows: number
    classNameArea: string
    classNameContainer: string
    placeholder: string
    onChange(event: React.SyntheticEvent): any
    value: string
    interviewId: string
    sendAnswer: (e: React.MouseEvent<HTMLButtonElement>, answerId: string) => void;
    spinner: boolean
    maxRows: number
    minRows: number
    sendButton: boolean
    button: boolean
    readOnly: boolean,
    name: string,
    updateAnswer: (answer: string) => void;
    handleFocus?: () => void;
    handleBlur?: () => void;
    recognition?: boolean
    isAudioFinished?: Boolean
    onFilePaste?: (file: File) => void;
    useModernLayout?: boolean;
    /** If set, mic triggers this (Realtime) instead of Web Speech dictation when both apply. */
    onVoiceConversationClick?: () => void;
    style?: TextAreaStyle;
}


const TextAreaForm = (props: textAreaProps) => {
    const { t } = useTranslation();
    const {
        button,
        name,
        sendButton,
        label,
        rows,
        classNameArea,
        classNameContainer,
        placeholder,
        onChange,
        value,
        interviewId,
        sendAnswer,
        spinner,
        maxRows,
        minRows,
        readOnly,
        updateAnswer,
        handleFocus,
        handleBlur,
        recognition,
        isAudioFinished,
        onFilePaste,
        useModernLayout = false,
        onVoiceConversationClick,
    } = props

    const [isListening, setIsListening] = React.useState(false);
    const recognitionRef = React.useRef<any>(null);

    // React.useEffect(() => {
    //     if (isAudioFinished && !isListening) {
    //         toggleListening()
    //     }

    // }, [isAudioFinished])


    const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter') {
            sendAnswer(event as any, interviewId);
            event.preventDefault();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!onFilePaste) return;

        const items = event.clipboardData?.items;
        if (!items) return;

        // Check if there's a file in the clipboard
        let hasFile = false;
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                hasFile = true;
                break;
            }
        }

        // If file found, prevent default paste and handle file
        if (hasFile) {
            event.preventDefault();
            event.stopPropagation();
            
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                        onFilePaste(file);
                    }
                    break;
                }
            }
        }
    };

    React.useEffect(() => {
        const windowWithSpeech = window as Window & {
            SpeechRecognition?: any;
            webkitSpeechRecognition?: any;
        };

        if (windowWithSpeech.SpeechRecognition || windowWithSpeech.webkitSpeechRecognition) {
            const SpeechRecognition = windowWithSpeech.SpeechRecognition || windowWithSpeech.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;

            recognitionRef.current.onresult = (event: any) => {
                const transcript = Array.from(event.results)
                    .map((result: any) => result[0])
                    .map((result: any) => result.transcript)
                    .join('');
                updateAnswer(transcript);
            };

            recognitionRef.current.onerror = (event: any) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

        } else {
            alert("Your browser does not support speech recognition. Please try a supported browser like Chrome.");
            console.error("Speech recognition is not supported in this browser.");
        }

    }, []);

    const toggleListening = () => {
        if (!recognitionRef.current) {
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            recognitionRef.current.start();
        }

        setIsListening(!isListening);
    };

    const showVoiceMic = Boolean(recognition || onVoiceConversationClick);
    const handleMicClick = () => {
        if (onVoiceConversationClick) {
            onVoiceConversationClick();
            return;
        }
        toggleListening();
    };

    const micTitle = onVoiceConversationClick
        ? t("views.processInterview.voiceConversationStart", { defaultValue: "Voice conversation" })
        : t("views.processInterview.voiceTooltipLaunch", { defaultValue: "Launch voice mode" });

    return (

        <>
            <div className={`${classNameContainer}`}>

                <TextareaAutosize
                    minRows={minRows}
                    maxRows={maxRows}
                    className={classNameArea}
                    placeholder={placeholder}
                    onChange={(event) => onChange(event)}
                    onKeyPress={handleKeyPress}
                    onPaste={handlePaste}
                    value={value}
                    name={name}
                    id="textarea"
                    rows={rows}
                    style={{
                        resize: 'none',
                        overflow: 'auto',
                        color: 'var(--text-primary)',
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: '0.75rem',
                        padding: '10px',
                        ...(props.style || {})
                    }}
                    readOnly={readOnly}
                    maxLength={3200}
                    onFocus={handleFocus}
                    onBlur={handleBlur}

                />

                {button &&
                    (useModernLayout ? (
                        <div className="input-actions">
                            <button
                                type="button"
                                className="input-btn send-btn"
                                onClick={(e) => { sendAnswer(e, interviewId) }}
                                disabled={spinner || !sendButton}
                                title="Send"
                            >
                                <svg viewBox="0 0 16 16" fill="none">
                                    <path d="M13 8L3 3L5.5 8L3 13L13 8Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                </svg>
                            </button>
                            {showVoiceMic && (
                                <button
                                    type="button"
                                    className={`input-btn voice-btn ${isListening && !onVoiceConversationClick ? 'recording' : ''}`}
                                    onClick={handleMicClick}
                                    title={micTitle}
                                >
                                    <svg viewBox="0 0 16 16" fill="none">
                                        <rect x="5" y="1" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.2" />
                                        <path d="M3 8C3 10.8 5.2 13 8 13C10.8 13 13 10.8 13 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                        <path d="M8 13V15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="d-flex position-absolute bottom-0 end-0">
                            {showVoiceMic &&
                                <Buttons
                                    buttonType="submit"
                                    name=""
                                    onClick={handleMicClick}
                                    isLoading={false}
                                    btnClass={`btn-outline-dark py-1 rounded-pill ms-1  mb-1 p-1 px-2`}
                                    labelClass="text-white"
                                    styleProps={{ height: 30, marginBottom: '0' }}
                                    withIcon={true}
                                    iconName={`fa-solid ${isListening && !onVoiceConversationClick ? 'bi bi-mic-mute text-theme' : 'bi bi-mic text-white'} fs-6 `}
                                    btnStyle={{ height: 58 }}
                                    arrowVisible={false}
                                    arrowColor=''
                                />
                            }

                            <Buttons
                                buttonType="submit"
                                name=""
                                onClick={(e) => { sendAnswer(e, interviewId) }}
                                isLoading={false}
                                btnClass={`btn-outline-dark rounded-pill ms-1 mb-1 me-2 p-1 px-2 ${(spinner || !sendButton) && 'disabled'} `}
                                labelClass="text-white fw-bold"
                                styleProps={{ height: 30, marginBottom: '0' }}
                                withIcon={true}
                                iconName={`fa-solid ${spinner ? 'fa-spinner' : "bi bi-send"} fs-6 text-theme`}
                                btnStyle={{ height: 58 }}
                                arrowVisible={false}
                                arrowColor=''
                            />
                        </div>
                    ))
                }
                {isListening && <RecordingMic onClick={toggleListening} />}

            </div>

        </>
    )
}
export default TextAreaForm